"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  addClientEmail,
  updateClientEmail,
  deleteClientEmail,
  setPrimaryClientEmail,
  addClientPhone,
  updateClientPhone,
  deleteClientPhone,
  setPrimaryClientPhone,
  CLIENT_GET,
  CLIENT_ALL_GET,
} from "@/api";
import { showErrorToast } from "@/lib/errorUtils";
import { ContactPhoneType } from "@/types";

/**
 * Mutation hooks for client contact CRUD (emails + phones).
 *
 * The contact lists render from the embedded `client.emailAddresses` /
 * `client.phoneNumbers` arrays, which are loaded as part of the client via
 * `[CLIENT_GET, clientId]`. There is no separate email/phone list query, so
 * every mutation simply invalidates `[CLIENT_GET, clientId]` and
 * `[CLIENT_ALL_GET]`. The resulting refetch reflects backend behaviour such as
 * primary-promotion-on-delete and the mirror writeback to
 * `client.email` / `client.phone`, keeping the details header, the contact
 * lists, and the clients table in sync.
 *
 * Errors surface through the centralized `showErrorToast(error, "client")`.
 */
export function useClientContacts(clientId: string) {
  const queryClient = useQueryClient();

  const invalidateClientData = () => {
    queryClient.invalidateQueries({ queryKey: [CLIENT_GET, clientId] });
    queryClient.invalidateQueries({ queryKey: [CLIENT_ALL_GET] });
  };

  const onContactError = (error: unknown) => showErrorToast(error, "client");

  // ---------------------------------------------------------------------------
  // Emails
  // ---------------------------------------------------------------------------
  const addEmail = useMutation({
    mutationFn: (input: { email: string; primary?: boolean }) =>
      addClientEmail(clientId, input),
    onSuccess: invalidateClientData,
    onError: onContactError,
  });

  const updateEmail = useMutation({
    mutationFn: ({ emailId, email }: { emailId: string; email: string }) =>
      updateClientEmail(clientId, emailId, { email }),
    onSuccess: invalidateClientData,
    onError: onContactError,
  });

  const deleteEmail = useMutation({
    mutationFn: (emailId: string) => deleteClientEmail(clientId, emailId),
    onSuccess: invalidateClientData,
    onError: onContactError,
  });

  const setPrimaryEmail = useMutation({
    mutationFn: (emailId: string) => setPrimaryClientEmail(clientId, emailId),
    onSuccess: invalidateClientData,
    onError: onContactError,
  });

  // ---------------------------------------------------------------------------
  // Phones
  // ---------------------------------------------------------------------------
  const addPhone = useMutation({
    mutationFn: (input: {
      number: string;
      type: ContactPhoneType;
      extension?: string;
      primary?: boolean;
    }) => addClientPhone(clientId, input),
    onSuccess: invalidateClientData,
    onError: onContactError,
  });

  const updatePhone = useMutation({
    mutationFn: ({
      phoneId,
      ...input
    }: {
      phoneId: string;
      number?: string;
      type?: ContactPhoneType;
      extension?: string;
    }) => updateClientPhone(clientId, phoneId, input),
    onSuccess: invalidateClientData,
    onError: onContactError,
  });

  const deletePhone = useMutation({
    mutationFn: (phoneId: string) => deleteClientPhone(clientId, phoneId),
    onSuccess: invalidateClientData,
    onError: onContactError,
  });

  const setPrimaryPhone = useMutation({
    mutationFn: (phoneId: string) => setPrimaryClientPhone(clientId, phoneId),
    onSuccess: invalidateClientData,
    onError: onContactError,
  });

  return {
    // Emails
    addEmail,
    updateEmail,
    deleteEmail,
    setPrimaryEmail,
    // Phones
    addPhone,
    updatePhone,
    deletePhone,
    setPrimaryPhone,
  };
}
